import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';
type ThemeMode = 'auto' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';
interface ThemeContextType {
  theme: ResolvedTheme;
  themeMode: ThemeMode;
  isLight: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
function getSystemTheme(): ResolvedTheme {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}
export function ThemeProvider({
  children,
  initialMode = 'light'
}: {
  children: React.ReactNode;
  /** Theme the provider starts in. Presentation surfaces mount in 'dark'. */
  initialMode?: ThemeMode;
}) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(initialMode);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme());
  // Listen for system theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const resolvedTheme: ResolvedTheme = themeMode === 'auto' ? systemTheme : themeMode;
  const toggleTheme = () => {
    setThemeMode(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      return resolvedTheme === 'light' ? 'dark' : 'light';
    });
  };
  useEffect(() => {
    document.body.className = resolvedTheme === 'light' ? 'bg-slate-50' : 'bg-dark';
  }, [resolvedTheme]);
  // A stable value keeps consumers from re-rendering (and remounting their
  // modals) every time an unrelated ancestor re-renders.
  const value = useMemo<ThemeContextType>(() => ({
    theme: resolvedTheme,
    themeMode,
    isLight: resolvedTheme === 'light',
    setThemeMode,
    toggleTheme
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [resolvedTheme, themeMode]);
  return <ThemeContext.Provider value={value} data-id="element-823" data-mp="12by7kxofi9h6">
      {children}
    </ThemeContext.Provider>;
}
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}